import './layout_grid.css';

class layout_grid extends HTMLElement {

    constructor() {
        super();
        this.attachShadow({
            mode: 'open'
        });
        this.items = [];
        this.resizeObserver = null;
        this.modal = null;
        this.modalContent = null;
        this.originalParent = null;
        this.originalNextSibling = null;
    }

    connectedCallback() {
        this.render();
        this.setupLayout();
    }

    disconnectedCallback() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    setupLayout() {
        const slot = this.shadowRoot.querySelector('slot');
        if (!slot) {
            return;
        }
        slot.addEventListener('slotchange',
            () => {
                this.prepareItems();
                this.calculateLayout();
            });
        this.resizeObserver = new ResizeObserver(() => {
            this.prepareItems();
            this.calculateLayout();
        });
        this.resizeObserver.observe(this);
    }

    prepareItems() {
        const slot = this.shadowRoot.querySelector('slot');
        if (!slot) {
            return;
        }
        this.items = slot.assignedElements().filter(element => element.nodeType === 1);
        this.items.forEach(
            (item, index) => {
                if (item.dataset.galleryReady === 'true') {
                    return;
                }
                item.dataset.galleryReady = 'true';
                /*
                 * Component name
                 */
                const name = item.dataset.name || item.getAttribute('aria-label') || `Component ${index + 1}`;
                /*
                 * Component description
                 */
                const description = item.dataset.description || 'No description available.';
                /*
                 * Save original component content
                 */
                const componentContent = document.createElement('div');
                componentContent.className = 'component-content';
                while (item.firstChild) {
                    componentContent.appendChild(item.firstChild);
                }
                /*
                 * Gallery title
                 */
                const title = document.createElement('div');
                title.className = 'gallery-title';
                title.textContent = name;

                /*
                * Add the title to the square
                */
                item.appendChild(title);

                /*
                * Store data
                */
                item._layoutGridData = {
                    name,
                    description,
                    componentContent
                };
                /*
                 * Base item styling
                 */
                item.classList.add('gallery-item');
                item.style.position = 'absolute';
                item.style.boxSizing = 'border-box';
                item.style.margin = '0';
                item.style.padding = '12px';
                item.style.overflow = 'hidden';
                item.style.cursor = 'pointer';
                item.style.border = '1px solid #b7b7b7ad';
                item.style.color = 'var(--general-color)';
                item.style.background = 'white';
                item.style.borderRadius = '2px';
                item.style.display = 'flex';
                item.style.alignItems = 'center';
                item.style.justifyContent = 'center';
                item.style.textAlign = 'center';
                item.style.transition = ['transform 350ms ease', 'box-shadow 250ms ease'].join(',');
                /*
                 * Hover effect
                 */
                item.addEventListener('mouseenter',
                    () => {
                        item.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.18)';
                    });
                item.addEventListener('mouseleave',
                    () => {
                        item.style.boxShadow = '';
                    });
                /*
                 * Open modal
                 */
                item.addEventListener('click', event => {
                    event.stopPropagation();
                    this.openModal(item);
                });
            });
    }

    calculateLayout() {
        if (!this.items.length) {
            return;
        }
        const container = this.shadowRoot.querySelector('.grid-container');
        if (!container) {
            return;
        }
        const containerWidth = this.clientWidth;
        if (containerWidth <= 0) {
            return;
        }
        /*
         * Square size:
         *
         * 10vw × 10vw
         */
        const squareSize = 100;
        /*
         * Gap:
         *
         * 1vw
         */
        const gap = 10;
        /*
         * Number of complete columns
         */
        const columns = Math.max(1, Math.floor(
            (containerWidth + gap) / (squareSize + gap)));
        /*
         * Center the complete grid
         *
         * This guarantees that the spacing
         * between every square is identical.
         */
        const gridWidth = columns * squareSize + (columns - 1) * gap;
        const leftOffset = Math.max(0,
            (containerWidth - gridWidth) / 2);
        /*
         * Position items
         */
        this.items.forEach(
            (item, index) => {
                const column = index % columns;
                const row = Math.floor(index / columns);
                const x = leftOffset + column * (squareSize + gap);
                const y = row * (squareSize + gap);
                item.style.width = `${squareSize}px`;
                item.style.height = `${squareSize}px`;
                item.style.transform = `translate(
                        ${x}px,
                        ${y}px
                    )`;
            });
        /*
         * Calculate total grid height
         */
        const rows = Math.ceil(this.items.length / columns);
        const totalHeight = rows * squareSize + Math.max(0, rows - 1) * gap;
        container.style.height = `${totalHeight}px`;
    }

    openModal(item) {
        if (!item || !item._layoutGridData) {
            return;
        }
        const data = item._layoutGridData;
        /*
         * Prevent multiple modals
         */
        if (this.modal) {
            this.closeModal();
        }
        /*
         * Create modal
         */
        this.modal = document.createElement('div');
        this.modal.className = 'layout-grid-modal';
        /*
         * Modal panel
         */
        const panel = document.createElement('div');
        panel.className = 'layout-grid-modal-panel';
        /*
         * Close button
         */
        const closeButton = document.createElement('button');
        closeButton.className = 'modal-close';
        closeButton.type = 'button';
        closeButton.textContent = '×';
        closeButton.setAttribute('aria-label', 'Close');
        closeButton.addEventListener('click', event => {
            event.stopPropagation();
            this.closeModal();
        });
        /*
         * Title
         */
        const title = document.createElement('h2');
        title.className = 'modal-title';
        title.textContent = data.name;
        /*
         * Explanation
         */
        const description = document.createElement('div');
        description.className = 'modal-description';
        description.textContent = data.description;
        /*
         * Component area
         */
        const content = document.createElement('div');
        content.className = 'modal-component';
        /*
         * Move the component content
         * into the modal.
         */
        content.appendChild(data.componentContent);
        /*
         * Build modal
         */
        panel.appendChild(closeButton);
        panel.appendChild(title);
        panel.appendChild(description);
        panel.appendChild(content);
        this.modal.appendChild(panel);
        /*
         * Add modal to body
         */
        document.body.appendChild(this.modal);
        /*
         * Clicking the backdrop closes
         */
        this.modal.addEventListener('click', event => {
            if (event.target === this.modal) {
                this.closeModal();
            }
        });
        /*
         * Prevent clicks inside
         * the component from closing it.
         */
        panel.addEventListener('click', event => {
            event.stopPropagation();
        });
        /*
         * Keep a reference to the item so the
         * component content can be restored on close.
         */
        this.modalContent = item;
        /*
         * Prevent page scrolling
         */
        document.body.style.overflow = 'hidden';
        /*
         * Keyboard close
         */
        this.handleKeyDown = event => {
            if (event.key === 'Escape') {
                this.closeModal();
            }
        };
        document.addEventListener('keydown', this.handleKeyDown);
    }

    closeModal() {
        if (!this.modal) {
            return;
        }
        /*
         * Restore component content
         */
        const component = this.modal.querySelector('.component-content');
        if (component && this.modalContent) {
            this.modalContent.appendChild(component);
        }
        /*
         * Remove modal
         */
        this.modal.remove();
        this.modal = null;
        this.modalContent = null;
        /*
         * Restore page scrolling
         */
        document.body.style.overflow = '';
    }
    render() {
        if (this.dataset.rendered) {
            return;
        }
        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: block;
                    position: relative;
                    width: 100%;
                }

                .grid-container {
                    position: relative;
                    width: 100%;
                    transition:
                        height 350ms ease;
                }
            </style>

            <div class="grid-container">
                <slot></slot>
            </div>
        `;
        this.dataset.rendered = "true";
    }
}
export {
    layout_grid
};